import React, { useState } from 'react';
import { 
  Mail, 
  MessageCircle, 
  Book, 
  Video,
  FileText,
  Search,
  ChevronDown,
  Send,
  ExternalLink
} from 'lucide-react';
import TutorialHint from './TutorialHint';
import { useAuth } from '../context/AuthContext';
import './Help.css';
import './AnimatedButtons.css';

function Help() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: user?.email || '',
    subject: '',
    message: ''
  });

  const faqs = [
    {
      id: 1,
      question: 'Como funciona o sistema de créditos de energia?',
      answer: 'Cada ação no Marketing PRO consome créditos de energia. Gerar um post consome 10 créditos, enquanto vídeos consomem de 50 a 160 créditos dependendo da duração. Você ganha 500 créditos ao se cadastrar e pode ganhar mais indicando amigos (100 créditos por indicação) ou fazendo upgrade para um plano pago.'
    },
    {
      id: 2,
      question: 'Posso agendar posts para publicação automática?',
      answer: 'Sim! No plano Profissional e Empresarial, você pode agendar seus posts para serem publicados automaticamente nas suas redes sociais conectadas. Basta criar o conteúdo, clicar em "Agendar" e escolher a data e hora desejadas.'
    },
    {
      id: 3,
      question: 'Como conectar minhas redes sociais?',
      answer: 'Vá em Configurações > Redes Sociais e clique em "Conectar" na rede social desejada. Você será redirecionado para autorizar o acesso e, após isso, poderá publicar diretamente das suas contas.'
    },
    {
      id: 4,
      question: 'Os vídeos gerados têm marca d\'água?',
      answer: 'No plano Gratuito, sim. Nos planos Profissional e Empresarial, todos os vídeos são gerados sem marca d\'água, prontos para publicação profissional.'
    },
    {
      id: 5,
      question: 'Posso cancelar minha assinatura a qualquer momento?',
      answer: 'Sim! Não há fidelidade. Você pode cancelar seu plano a qualquer momento em Configurações > Planos e Pagamento. Você continuará tendo acesso até o final do período já pago.'
    },
    {
      id: 6,
      question: 'Como funciona o treinamento da IA com meus dados?',
      answer: 'Na aba "Treinar IA", você pode adicionar informações sobre sua marca, produtos, tom de voz e exemplos de conteúdo. A IA aprende com esses dados e gera conteúdos cada vez mais alinhados com sua identidade de marca.'
    },
    {
      id: 7,
      question: 'Quantos usuários podem acessar a conta?',
      answer: 'No plano Gratuito e Profissional, apenas 1 usuário. No plano Empresarial, você pode ter 10 ou mais usuários com diferentes níveis de permissão.'
    },
    {
      id: 8,
      question: 'Como funciona a edição de imagens?',
      answer: 'Usamos IA avançada (Imagen 3) para editar imagens. Você pode mascarar áreas da imagem e adicionar ou remover objetos usando apenas texto. Por exemplo: "adicionar uma xícara de café" ou "remover o fundo".'
    }
  ];

  const resources = [
    {
      icon: Book,
      title: 'Central de Ajuda',
      description: 'Artigos e guias completos',
      link: '#',
      tone: 'green'
    },
    {
      icon: Video,
      title: 'Tutoriais em Vídeo',
      description: 'Aprenda assistindo',
      link: '#',
      tone: 'purple'
    },
    {
      icon: FileText,
      title: 'Documentação da API',
      description: 'Para desenvolvedores',
      link: '#',
      tone: 'blue'
    },
    {
      icon: MessageCircle,
      title: 'Comunidade',
      description: 'Participe do Discord',
      link: '#',
      tone: 'red'
    }
  ];

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmitSupport = (e) => {
    e.preventDefault();
    if (!supportForm.name || !supportForm.email || !supportForm.subject || !supportForm.message) {
      alert('Por favor, preencha todos os campos!');
      return;
    }
    alert('Sua mensagem foi enviada! Nossa equipe responderá em até 24 horas.');
    setSupportForm({ ...supportForm, subject: '', message: '' });
  };

  return (
    <div className="help-container">
      <div className="search-help">
        <Search size={20} />
        <input
          type="text"
          placeholder="Buscar por ajuda..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <TutorialHint text="Filtra perguntas frequentes e artigos relacionados ao seu problema." />
      </div>

      <section className="help-resources">
        <h2 className="help-section-title">Recursos adicionais</h2>
        {resources.map((resource, index) => {
          const IconComponent = resource.icon;
          return (
            <a 
              key={index} 
              href={resource.link} 
              className={`resource-card resource-card--${resource.tone}`}
            >
              <div className="resource-icon">
                <IconComponent size={24} />
              </div>
              <h3>{resource.title}</h3>
              <p>{resource.description}</p>
              <ExternalLink size={16} className="external-icon" />
            </a>
          );
        })}
      </section>

      <div className="faq-section">
        <h2>Perguntas Frequentes</h2>
        <div className="faq-list">
          {filteredFaqs.length === 0 ? (
            <p className="no-results">Nenhuma pergunta encontrada. Tente outro termo de busca.</p>
          ) : (
            filteredFaqs.map(faq => (
              <div 
                key={faq.id} 
                className={`faq-item ${openFaq === faq.id ? 'open' : ''}`}
                onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
              >
                <div className="faq-question">
                  <h3>{faq.question}</h3>
                  <ChevronDown 
                    size={20} 
                    className={`chevron ${openFaq === faq.id ? 'rotated' : ''}`}
                  />
                </div>
                {openFaq === faq.id && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <section className="contact-support">
        <h2 className="help-section-title">Contato com suporte</h2>
        <div className="support-header">
          <Mail size={32} />
          <h2>Ainda precisa de ajuda?</h2>
          <p>Entre em contato com nossa equipe de suporte</p>
        </div>

        <form onSubmit={handleSubmitSupport} className="support-form">
          <div className="form-row">
            <div className="form-group">
              <label>Nome</label>
              <input
                type="text"
                value={supportForm.name}
                onChange={(e) => setSupportForm({ ...supportForm, name: e.target.value })}
                placeholder="Seu nome"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={supportForm.email}
                onChange={(e) => setSupportForm({ ...supportForm, email: e.target.value })}
                placeholder="seu@email.com"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Assunto</label>
            <select
              value={supportForm.subject}
              onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
            >
              <option value="">Selecione um assunto</option>
              <option value="technical">Problema Técnico</option>
              <option value="billing">Dúvida sobre Cobrança</option>
              <option value="feature">Sugestão de Funcionalidade</option>
              <option value="account">Problema com a Conta</option>
              <option value="other">Outro</option>
            </select>
          </div>

          <div className="form-group">
            <label>Mensagem</label>
            <textarea
              rows="6"
              value={supportForm.message}
              onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
              placeholder="Descreva sua dúvida ou problema..."
            ></textarea>
          </div>

          <button type="submit" className="btn-animated btn-animated-full">
            <span>
              <Send size={18} />
              Enviar Mensagem
            </span>
          </button>
        </form>

        <div className="contact-info">
          <p>
            <Mail size={16} />
            <span>suporte@marketingpro.app.br</span>
          </p>
          <p>
            <MessageCircle size={16} />
            <span>Chat ao vivo: Segunda a Sexta, 9h-18h</span>
          </p>
        </div>
      </section>
    </div>
  );
}

export default Help;
